/* eslint-disable no-restricted-globals */
import React, { useState, useEffect } from "react"
import { Flex, Box } from "theme-ui"
import _ from "lodash"
import graphql from "babel-plugin-relay/macro"
import Creatable from "react-select/creatable"
import { faEllipsisH, faPlus } from '@fortawesome/free-solid-svg-icons'

import NewBoxButton from "../../components/Radiate/NewBoxButton/NewBoxButton"
import DropdownMenu from "../../components/Radiate/DropdownMenu/DropdownMenu"
import {
  useMutation
} from "react-relay"
import { pureLocaleList, randomString } from "../util"

import * as Styles from "./LocaleGroupsSettingsStyles"

const LocaleGroupsSettingsMutation = graphql`
  mutation LocaleGroupsSettings_updateBusinessMutation ($input: UpdateBusinessInput!) {
    updateBusiness(input: $input) {
      business {
        id
        businessId
        localeGroups {
          id
          name
          locales
          default
        }
        chatConfiguration {
          themeColor
          customCss
          showAgentProfile
          localised {
            localeId
            persistentMenu {
              buttons
              enable
            }
            iceBreakers {
              ctas
              enable
            }
            inputPlaceholder
          }
        }
      }
    }
  }
`


const LocaleGroupsSettings = ({
  info
}) => {
  const [localeGroups, setLocaleGroups] = useState(info?.localeGroups)
  const [removedLocaleGroups, setRemovedLocaleGroups] = useState([])
  const localeList = pureLocaleList.map((l) => {
    return ({
      "label": l.text,
      "value": l.value
    })
  })

  const [commit, isInFlight] = useMutation(
    LocaleGroupsSettingsMutation
  )

  useEffect(() => {
    const haveDefault = localeGroups.find(o => o.default)
    if (!haveDefault) {
      let clone = _.cloneDeep(localeGroups)
      if (clone?.length) {
        clone[0].default = true
        setLocaleGroups(clone)
      }
    }
  }, [localeGroups?.length])

  return (
    <Box>
      <Flex py={3} sx={{ justifyContent: "space-between" }}>
        <NewBoxButton
          primary
          inverted
          text="Locale Group"
          icon={faPlus}
          onClick={() => {
            const modLgs = [
              ...localeGroups,
              {
                id: randomString(8),
                name: "",
                locales: [],
                default: localeGroups?.length ? false : true,
              }
            ]
            setLocaleGroups(modLgs)
          }}
        />
        <NewBoxButton
          text="Save"
          loading={isInFlight}
          primary
          disabled={_.isEqual(localeGroups, info?.localeGroups)}
          onClick={() => {
            let localised = []
            info?.chatConfiguration?.localised?.forEach((o) => {
              const removed = removedLocaleGroups.find(r => r === o.localeId)
              if (!removed) {
                localised = [...localised, o]
              }
            })
            const saveCommit = () => {
              commit({
                variables: {
                  input: {
                    businessId: info?.businessId,
                    localeGroups: localeGroups,
                    localised: localised
                  },
                },
                onCompleted: (response, errs) => { },
                onError: (err) => {
                  console.log(err)
                }
              })
            }
            const noName = localeGroups.find(o => o.name === "")
            const nolocales = localeGroups.find(o => o.default !== true && !o.locales?.length)
            if (noName) {
              return alert("Name must not be empty.")
            }
            if (nolocales) {
              return alert("Locales must not be empty.")
            }
            if (removedLocaleGroups?.length) {
              const c = confirm("It may affect the ice breakers and persistent menu settings. Are you sure you want to update the locale groups settings?")
              if (c) {
                saveCommit()
              }
            } 
            saveCommit()
          }}
        />
      </Flex>
      <Styles.StyledTable
        simple
        data={localeGroups}
        displayFields={[
          {
            label: "Default",
            key: ["default", "id"],
            custom: (value, id) => {
              return (
                <>
                  <Styles.RadioButtonBorder
                    selected={value}
                    onClick={() => {
                      const index = localeGroups.findIndex(o => o.id === id)
                      let modLgs = localeGroups.map((ml) => {
                        return ({
                          id: ml.id,
                          name: ml.name,
                          locales: ml.locales,
                          default: false,
                        })
                      })
                      modLgs = [
                        ...modLgs.slice(0, index),
                        {
                          id: modLgs[index].id,
                          name: modLgs[index].name,
                          locales: modLgs[index].locales,
                          default: true,
                        },
                        ...modLgs.slice(index + 1)
                      ]
                      setLocaleGroups(modLgs)
                    }}
                  >
                    <Styles.RadioButton
                      className="radio-button"
                      selected={value}
                    />
                  </Styles.RadioButtonBorder>
                </>
              )
            }
          },
          {
            label: "Locale Group",
            key: ["name", "id"],
            custom: (name, id) => {
              return (
                <Styles.NameTextField
                  borderStyle
                  value={name}
                  placeholder="Name"
                  onChange={(e) => {
                    const index = localeGroups.findIndex(o => o.id === id)
                    let modLgs
                    modLgs = [
                      ...localeGroups.slice(0, index),
                      {
                        id: localeGroups[index].id,
                        name: e.target.value,
                        locales: localeGroups[index].locales,
                        default: localeGroups[index].default
                      },
                      ...localeGroups.slice(index + 1)
                    ]
                    setLocaleGroups(modLgs)
                  }}
                />
              )
            }
          },
          {
            label: "Locales",
            key: ["locales", "id"],
            custom: (locales, id) => {
              const values = locales.map((o) => {
                const target = localeList.find(l => l.value === o)
                if (target) {
                  return ({
                    label: target.label,
                    value: o
                  })
                }
                return ({
                  label: o,
                  value: o
                })
              })
              return (
                <Creatable
                  isMulti
                  isClearable
                  value={values}
                  options={localeList}
                  onChange={(value) => {
                    const index = localeGroups.findIndex(o => o.id === id)
                    let modLg
                    if (value) {
                      modLg = [
                        ...localeGroups.slice(0, index),
                        {
                          id: localeGroups[index].id,
                          name: localeGroups[index].name,
                          locales: value.map(o => o.value),
                          default: localeGroups[index].default
                        },
                        ...localeGroups.slice(index + 1)
                      ]
                    } else {
                      modLg = [
                        ...localeGroups.slice(0, index),
                        {
                          id: localeGroups[index].id,
                          name: localeGroups[index].name,
                          locales: [],
                          default: localeGroups[index].default
                        },
                        ...localeGroups.slice(index + 1)
                      ]
                    }
                    setLocaleGroups(modLg)
                  }}
                />
              )
            }
          }, {
            label: "",
            key: ["id", "default"],
            custom: (id, isDefault) => {
              return (
                <Flex>
                  <DropdownMenu
                    className="locale-content-group-button"
                    key="delete-button"
                    icon={faEllipsisH}
                    inverted
                    placement="bottom-end"
                    buttons={[
                      {
                        label: "Remove",
                        danger: true,
                        disabled: isDefault,
                        onClick: () => {
                          const modLgs = localeGroups.filter(o => o.id !== id)
                          setLocaleGroups(modLgs)
                          const array = [...removedLocaleGroups, id]
                          setRemovedLocaleGroups(array)
                        }
                      }
                    ]}
                  />
                </Flex>
              )
            }
          }
        ]}
      />
    </Box>
  )
}

export default LocaleGroupsSettings
